import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { RadioGroupField } from '@design-system/DataForm/RadioGroupField';
import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import useRegisterStore, {
  RegistrationTrack,
} from '@/hooks/useRegistrationStore';
import { useLocation, useNavigate } from 'react-router-dom';
import * as P from '@/constants/paths';

const addressSchema = z.object({
  selectedAddress: z.string(),
});

type AddressForm = z.infer<typeof addressSchema>;

export const RegistrationAddressConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const addressData = location.state?.addressData;

  const {
    setAddressDetails,
    phone,
    phoneCode,
    addressType,
    setAddressType,
    setRestrictedRegistrationSteps,
  } = useRegisterStore();

  const defaultSelectedAddress =
    addressData.find((item: any) => item.selected)?.type || addressData[0].type;

  const form = useForm<AddressForm>({
    mode: 'onSubmit',
    resolver: zodResolver(addressSchema),
    defaultValues: {
      selectedAddress: addressType || defaultSelectedAddress,
    },
  });

  const handleGoBack = () => {
    navigate(-1);
  };

  const onSubmit = () => {
    const addressObj = addressData.find(
      (item: any) => item.type === form.getValues('selectedAddress'),
    );
    if (addressObj) {
      setAddressDetails({
        street: addressObj?.address?.addressLine1 ?? '',
        apartment: addressObj?.address?.addressLine2 ?? '',
        city: addressObj?.address?.city ?? '',
        state: addressObj?.address?.state ?? '',
        zip: addressObj?.address?.zip ?? '',
        country: addressObj?.address?.country ?? '',
        phoneCode,
        phone,
      });
      setAddressType(form.getValues('selectedAddress'));
    }
    Promise.all([
      setRestrictedRegistrationSteps([
        RegistrationTrack.TermsOfService,
        RegistrationTrack.ConsentForm,
      ]),
      navigate(P.REGISTRATION_CONFIRMATION),
    ]);
  };

  const renderItem = (title: string, address: string) => {
    return (
      <div className="flex flex-col gap-2 text-left" data-testid="registrationAddressConfirm-addressItem">
        <p className="typography-h3" data-testid="registrationAddressConfirm-addressTitle">{title}</p>
        <p className="typography-btn-large" data-testid="registrationAddressConfirm-addressText">{address}</p>
      </div>
    );
  };

  const renderAddressFormat = (item: any) => {
    const title = item.formattedAddress ? 'Suggested Address' : 'Your Input';

    const addressParts = [
      item.address?.addressLine1,
      item.address?.addressLine2,
      item.address?.city,
      item.address?.state,
      item.address?.country,
      item.address?.zip,
    ].filter(Boolean);

    const address = addressParts.join(', ');
    return renderItem(title, address);
  };

  return (
    <RegistrationScreen
      form={form}
      title="Confirm Address"
      content={
        <RadioGroupField
          name="selectedAddress"
          options={addressData.map((item: any) => ({
            label: renderAddressFormat(item),
            value: item.type,
          }))}
          className="mt-5"
          itemsClass="h-full"
          labelsClassName="text-lg cursor-pointer"
        />
      }
      primaryActionLabel={
        form.getValues('selectedAddress') === 'input'
          ? 'Keep My Edit'
          : 'Use Suggestion'
      }
      previousPath={P.REGISTRATION_CONTACT_INFORMATION}
      primaryActionIsSubmit
      secondaryActionLabel="Edit Address"
      secondaryActionOnClick={handleGoBack}
      formOnValid={onSubmit}
      checkForRegisterStore={RegistrationTrack.AddressConfirmation}
      data-testid="registrationAddressConfirm-screen"
    />
  );
};
